import { TEXTOS } from '../../../lib/glossario'
import type { LinhaDeUsoDaIa } from '../../../types/usoDaIa'
import { competenciaPorExtenso } from '../../chamados/competencia'

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 })
const SEGUNDOS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })

/**
 * Como cada tipo de análise se chama na tela.
 *
 * O tipo vem do `escopo` da linha, e não de um literal na consulta — então um escopo novo chega aqui
 * sozinho. Sem o rótulo, a tabela mostraria o código cru (`COMPETENCIA`) para quem não é de TI.
 */
const ROTULO_DO_TIPO: Record<string, string> = {
  CHAMADO: 'Parecer de chamado',
  GMUD: 'Parecer de solicitação de mudança',
  COMPETENCIA: 'Leitura do fechamento do mês',
  REPOSITORIOS: 'Resumo da atividade nos repositórios',
  DOCUMENTOS: 'Perguntas ao contrato',
}

/**
 * O consumo por mês, tipo e modelo — em unidades de uso, nunca em reais.
 *
 * **Três categorias de origem, e não duas.** Automática é a leitura que nasceu sozinha; a pedido é o
 * botão; em lote é a análise de uma competência inteira. Com duas categorias, os pareceres de um
 * lote de 307 chamados apareceriam como "automáticos" e matariam o número que diz quanto do custo
 * acontece sem clique de gente — justamente no mês em que ele mais importa.
 *
 * @param props as linhas do relatório
 * @returns a tabela
 */
export function TabelaDeUso({ linhas }: { linhas: LinhaDeUsoDaIa[] }) {
  const t = TEXTOS.usoDaIa
  return (
    <div className="overflow-x-auto rounded-lg ring-1 ring-slate-200">
      <table className="min-w-full text-sm">
        <caption className="sr-only">{t.ajuda}</caption>
        <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
          <tr>
            <th scope="col" className="px-3 py-2 font-semibold">
              Competência
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Tipo
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              {t.modeloUsado}
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Execuções
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              {t.origem.automatica}
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              {t.origem.emLote}
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Falhas
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Unidades de uso
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Duração média
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {linhas.map((linha) => (
            <Linha key={`${linha.competencia}-${linha.tipo}-${linha.modelo ?? '—'}`} linha={linha} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Um mês, um tipo e um modelo. */
function Linha({ linha }: { linha: LinhaDeUsoDaIa }) {
  const t = TEXTOS.usoDaIa
  return (
    <tr className="align-top">
      <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium text-slate-900">
        {competenciaPorExtenso(linha.competencia)}
      </th>
      <td className="px-3 py-2 text-slate-700">{ROTULO_DO_TIPO[linha.tipo] ?? linha.tipo}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{linha.modelo ?? '—'}</td>
      <td className="px-3 py-2 tabular-nums text-slate-900">{INTEIRO.format(linha.execucoes)}</td>
      <td className="px-3 py-2 tabular-nums text-slate-700">{INTEIRO.format(linha.automaticas)}</td>
      <td className="px-3 py-2 tabular-nums text-slate-700">{INTEIRO.format(linha.emLote)}</td>
      <td className={linha.falhas > 0 ? 'px-3 py-2 tabular-nums text-red-800' : 'px-3 py-2 tabular-nums text-slate-500'}>
        {INTEIRO.format(linha.falhas)}
      </td>
      <td className="px-3 py-2 tabular-nums text-slate-900">
        {INTEIRO.format(linha.tokensDeEntrada + linha.tokensDeSaida)}
        {linha.semMedicao > 0 && (
          <span className="mt-0.5 block text-xs font-normal text-amber-800">{t.semMedicao(linha.semMedicao)}</span>
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-slate-700">
        {linha.duracaoMediaSegundos == null ? '—' : `${SEGUNDOS.format(linha.duracaoMediaSegundos)} s`}
      </td>
    </tr>
  )
}
